import type { Dispatch, ReactElement, Ref, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { TestOrderInput } from '@/features/patient-record/hooks/useTestOrderMutations';
import type {
  TestOrderSampleType,
  TestOrderStatus,
  TestOrderType,
} from '@/features/patient-record/types';

import { FormField } from '../_shared/FormField';

const TEST_TYPE_OPTIONS: readonly TestOrderType[] = [
  'SINGLE_GENE',
  'GENE_PANEL',
  'WES',
  'WGS',
  'KARYOTYPE',
  'MICROARRAY',
  'MLPA',
  'OTHER',
];

const SAMPLE_TYPE_OPTIONS: readonly TestOrderSampleType[] = [
  'BLOOD',
  'SALIVA',
  'BUCCAL_SWAB',
  'TISSUE',
  'DRIED_BLOOD_SPOT',
  'OTHER',
];

const STATUS_OPTIONS: readonly TestOrderStatus[] = [
  'ORDERED',
  'SAMPLE_SENT',
  'RESULTED',
  'REVIEWED',
  'CANCELLED',
];

interface TestOrderFormFieldsProps {
  readonly form: TestOrderInput;
  readonly setForm: Dispatch<SetStateAction<TestOrderInput>>;
  readonly focusRef: Ref<HTMLInputElement>;
  /**
   * When `true`, render the status select as a read-only "Cancelled"
   * row instead of an editable dropdown. Cancelled is a terminal status
   * transition (per the Phase 1 spec) — other fields stay editable so
   * the user can still correct details, but they can't revive a
   * cancelled order from the form. Switch back via a new order.
   */
  readonly lockStatusToCancelled?: boolean;
}

export function TestOrderFormFields({
  form,
  setForm,
  focusRef,
  lockStatusToCancelled = false,
}: TestOrderFormFieldsProps): ReactElement {
  const { t } = useTranslation();

  const setText = (key: keyof TestOrderInput) => (value: string): void => {
    setForm((prev) => ({ ...prev, [key]: value.length > 0 ? value : null }));
  };

  const showResultFields =
    form.status === 'RESULTED' || form.status === 'REVIEWED';

  // Two-column grid so the ~15 fields fit a wide modal without scrolling. Long
  // fields (name, free-text, notes) span the full width; the rest auto-flow into
  // paired columns.
  const full = 'sm:col-span-2';

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
      <FormField label={t('patient.testOrders.testName')} required className={full}>
        <Input
          ref={focusRef}
          type="text"
          value={form.testName}
          placeholder={t('patient.testOrders.testNamePlaceholder')}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, testName: event.target.value }));
          }}
        />
      </FormField>

      <FormField label={t('patient.testOrders.testType')}>
        <Select
          value={form.testType}
          onChange={(event) => {
            setForm((prev) => ({
              ...prev,
              testType: event.target.value as TestOrderType,
            }));
          }}
        >
          {TEST_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`patient.testOrders.testTypeOption.${option}`)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label={t('patient.testOrders.testCode')}>
        <Input
          type="text"
          value={form.testCode ?? ''}
          placeholder={t('patient.testOrders.testCodePlaceholder')}
          onChange={(event) => {
            setText('testCode')(event.target.value);
          }}
        />
      </FormField>

      {form.testType === 'GENE_PANEL' && (
        <FormField label={t('patient.testOrders.panelName')} className={full}>
          <Input
            type="text"
            value={form.panelName ?? ''}
            placeholder={t('patient.testOrders.panelNamePlaceholder')}
            onChange={(event) => {
              setText('panelName')(event.target.value);
            }}
          />
        </FormField>
      )}

      <FormField label={t('patient.testOrders.labProvider')}>
        <Input
          type="text"
          value={form.labProvider ?? ''}
          placeholder={t('patient.testOrders.labProviderPlaceholder')}
          onChange={(event) => {
            setText('labProvider')(event.target.value);
          }}
        />
      </FormField>
      <FormField label={t('patient.testOrders.orderedByExternal')}>
        <Input
          type="text"
          value={form.orderedByExternal ?? ''}
          placeholder={t('patient.testOrders.orderedByExternalPlaceholder')}
          onChange={(event) => {
            setText('orderedByExternal')(event.target.value);
          }}
        />
      </FormField>

      <FormField label={t('patient.testOrders.orderedDateLabel')} required>
        <DateInput
          value={form.orderedDate}
          onChange={(value) => {
            setForm((prev) => ({ ...prev, orderedDate: value }));
          }}
        />
      </FormField>
      <FormField label={t('patient.testOrders.targetDeliveryDate')}>
        <DateInput
          value={form.targetDeliveryDate ?? ''}
          onChange={(value) => {
            setForm((prev) => ({
              ...prev,
              targetDeliveryDate: value.length > 0 ? value : null,
            }));
          }}
        />
      </FormField>

      <FormField label={t('patient.testOrders.sampleType')}>
        <Select
          value={form.sampleType ?? ''}
          onChange={(event) => {
            const next = event.target.value;
            setForm((prev) => ({
              ...prev,
              sampleType: next.length > 0 ? (next as TestOrderSampleType) : null,
            }));
          }}
        >
          <option value="">{t('patient.testOrders.sampleTypeUnset')}</option>
          {SAMPLE_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`patient.testOrders.sampleTypeOption.${option}`)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label={t('patient.testOrders.specimen')}>
        <Input
          type="text"
          value={form.specimen ?? ''}
          placeholder={t('patient.testOrders.specimenPlaceholder')}
          onChange={(event) => {
            setText('specimen')(event.target.value);
          }}
        />
      </FormField>

      <FormField label={t('patient.testOrders.sampleCollectedDate')}>
        <DateInput
          value={form.sampleCollectedDate ?? ''}
          onChange={(value) => {
            setForm((prev) => ({
              ...prev,
              sampleCollectedDate: value.length > 0 ? value : null,
            }));
          }}
        />
      </FormField>
      <FormField label={t('patient.testOrders.sampleSentDate')}>
        <DateInput
          value={form.sampleSentDate ?? ''}
          onChange={(value) => {
            setForm((prev) => ({
              ...prev,
              sampleSentDate: value.length > 0 ? value : null,
            }));
          }}
        />
      </FormField>

      <FormField label={t('patient.testOrders.statusFieldLabel')}>
        {lockStatusToCancelled ? (
          <div className="flex flex-col gap-1">
            <div className="inline-flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground">
              {t('patient.testOrders.status.CANCELLED')}
            </div>
            <p className="text-[11px] text-muted-foreground italic">
              {t('patient.testOrders.cancelledStatusLockedHint')}
            </p>
          </div>
        ) : (
          <Select
            value={form.status ?? 'ORDERED'}
            onChange={(event) => {
              setForm((prev) => ({
                ...prev,
                status: event.target.value as TestOrderStatus,
              }));
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`patient.testOrders.status.${option}`)}
              </option>
            ))}
          </Select>
        )}
      </FormField>
      {showResultFields && (
        <FormField label={t('patient.testOrders.resultDate')}>
          <DateInput
            value={form.resultDate ?? ''}
            onChange={(value) => {
              setForm((prev) => ({
                ...prev,
                resultDate: value.length > 0 ? value : null,
              }));
            }}
          />
        </FormField>
      )}

      <FormField label={t('patient.testOrders.clinicalIndication')} className={full}>
        <Textarea
          value={form.indication ?? ''}
          placeholder={t('patient.testOrders.clinicalIndicationPlaceholder')}
          rows={2}
          onChange={(event) => {
            setText('indication')(event.target.value);
          }}
        />
      </FormField>

      {showResultFields && (
        <>
          <FormField label={t('patient.testOrders.resultSummary')} className={full}>
            <Textarea
              value={form.resultSummary ?? ''}
              placeholder={t('patient.testOrders.resultSummaryPlaceholder')}
              rows={3}
              onChange={(event) => {
                setText('resultSummary')(event.target.value);
              }}
            />
          </FormField>
          <FormField label={t('patient.testOrders.externalReportUrl')} className={full}>
            <Input
              type="url"
              value={form.externalReportUrl ?? ''}
              placeholder={t('patient.testOrders.externalReportUrlPlaceholder')}
              onChange={(event) => {
                setText('externalReportUrl')(event.target.value);
              }}
            />
          </FormField>
        </>
      )}

      <FormField label={t('patient.testOrders.notes')} className={full}>
        <Textarea
          value={form.notes ?? ''}
          placeholder={t('patient.testOrders.notesPlaceholder')}
          rows={2}
          onChange={(event) => {
            setText('notes')(event.target.value);
          }}
        />
      </FormField>
    </div>
  );
}
